import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { 
  View, 
  Text, 
  ScrollView, 
  TouchableOpacity, 
  Dimensions,
  Alert,
  Platform
} from 'react-native';
import { PanGestureHandler, State } from 'react-native-gesture-handler';
import { 
  XStack, 
  YStack, 
  Card, 
  Button,
  Progress
} from 'tamagui';
import { 
  MaterialIcons, 
  Feather, 
  AntDesign,
  MaterialCommunityIcons 
} from '@expo/vector-icons';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import * as Animatable from 'react-native-animatable';
import Svg, { Line, Rect, Text as SvgText, Circle } from 'react-native-svg';

interface Task {
  _id: string;
  title: string;
  description?: string;
  status: 'todo' | 'in-progress' | 'review' | 'done';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  assignedTo?: string[];
  dueDate?: string;
  startDate?: string;
  createdAt: string;
  updatedAt: string;
  workPackageId: string;
  timeTracking?: {
    estimated: number;
    actual: number;
  };
  dependencies?: string[];
}

interface WorkPackage {
  _id: string;
  title: string;
  description?: string;
  tasks: Task[];
  status: 'active' | 'completed' | 'archived';
  startDate?: string;
  endDate?: string;
  progress: number;
}

interface Project {
  _id: string;
  title: string;
  description?: string;
  status: string;
  progress: number;
  startDate: string;
  endDate: string;
  team: any[];
  workPackages: WorkPackage[];
}

interface ModernGanttChartProps {
  project: Project;
  workPackages: WorkPackage[];
  tasks: Task[];
  onTaskEdit: (task: Task) => void;
}

const ModernGanttChart: React.FC<ModernGanttChartProps> = ({
  project,
  workPackages,
  tasks,
  onTaskEdit
}) => {
  const theme = themeSelector();
  const screenDimensions = useMemo(() => Dimensions.get('window'), []);
  const { width: screenWidth, height: screenHeight } = screenDimensions;
  
  const [timelineView, setTimelineView] = useState<'days' | 'weeks' | 'months'>('weeks');
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [scrollOffset, setScrollOffset] = useState(0);
  const [showCriticalPath, setShowCriticalPath] = useState(false);
  const [ganttWidth, setGanttWidth] = useState(screenWidth * 2);

  const scrollViewRef = useRef<ScrollView>(null);
  const timelineScrollRef = useRef<ScrollView>(null);

  // Memoized date calculations to prevent recalculation
  const projectDates = useMemo(() => {
    const startDate = new Date(project?.startDate || new Date());
    const endDate = new Date(project?.endDate || new Date());
    const duration = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
    
    return { startDate, endDate, duration };
  }, [project?.startDate, project?.endDate]);

  // Memoized layout constants
  const layoutConstants = useMemo(() => {
    const dayWidth = 40 * zoomLevel;
    return {
      dayWidth,
      weekWidth: dayWidth * 7,
      monthWidth: dayWidth * 30,
      rowHeight: 60,
      headerHeight: 80
    };
  }, [zoomLevel]);

  // Component cleanup effect
  useEffect(() => {
    return () => {
      setSelectedTask(null);
      setScrollOffset(0);
    };
  }, []);

  // Generate timeline dates with memoization
  const generateTimeline = useCallback(() => {
    const timeline = [];
    const current = new Date(projectDates.startDate);
    
    while (current <= projectDates.endDate) {
      timeline.push(new Date(current));
      
      switch (timelineView) {
        case 'days':
          current.setDate(current.getDate() + 1);
          break;
        case 'weeks':
          current.setDate(current.getDate() + 7);
          break;
        case 'months':
          current.setMonth(current.getMonth() + 1);
          break;
      }
    }
    
    return timeline;
  }, [projectDates.startDate, projectDates.endDate, timelineView]);

  const timeline = useMemo(() => generateTimeline(), [generateTimeline]);

  // Calculate task position and width with memoization
  const getTaskPosition = useCallback((task: Task) => {
    const taskStart = new Date(task.startDate || task.createdAt);
    const taskEnd = new Date(task.dueDate || taskStart);
    
    const startOffset = Math.max(0, (taskStart.getTime() - projectDates.startDate.getTime()) / (1000 * 60 * 60 * 24));
    const duration = Math.max(1, (taskEnd.getTime() - taskStart.getTime()) / (1000 * 60 * 60 * 24));
    
    return {
      left: startOffset * layoutConstants.dayWidth,
      width: duration * layoutConstants.dayWidth,
      startDate: taskStart,
      endDate: taskEnd,
      duration
    };
  }, [projectDates.startDate, layoutConstants.dayWidth]);

  // Memoized utility functions
  const getStatusColor = useCallback((status: string) => {
    switch (status) {
      case 'todo': return '#6b7280';
      case 'in-progress': return '#3b82f6';
      case 'review': return '#f59e0b';
      case 'done': return '#10b981';
      default: return '#6b7280';
    }
  }, []);

  const getPriorityColor = useCallback((priority: string) => {
    switch (priority) {
      case 'urgent': return '#ef4444';
      case 'high': return '#f97316';
      case 'medium': return '#eab308';
      case 'low': return '#22c55e';
      default: return '#6b7280';
    }
  }, []);

  const formatTimelineDate = useCallback((date: Date) => {
    try {
      switch (timelineView) {
        case 'days':
          return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        case 'weeks':
          return `Week ${Math.ceil(date.getDate() / 7)}`;
        case 'months':
          return date.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
        default:
          return date.toLocaleDateString();
      }
    } catch (error) {
      console.warn('Date formatting error:', error);
      return '';
    }
  }, [timelineView]);

  // Handle task bar press with cleanup
  const handleTaskPress = useCallback((task: Task) => {
    setSelectedTask(task);
    onTaskEdit(task);
  }, [onTaskEdit]);

  // Handle zoom with constraints
  const handleZoom = useCallback((delta: number) => {
    const newZoom = Math.max(0.5, Math.min(3, zoomLevel + delta));
    setZoomLevel(newZoom);
    setGanttWidth(screenWidth * 2 * newZoom);
  }, [zoomLevel, screenWidth]);

  // Render timeline header
  const renderTimelineHeader = () => (
    <View style={{
      height: layoutConstants.headerHeight,
      backgroundColor: theme ? '#ffffff' : '#1f2937',
      borderBottomWidth: 1,
      borderBottomColor: theme ? '#e5e7eb' : '#374151',
    }}>
      {/* Timeline Controls */}
      <XStack 
        justifyContent="space-between" 
        alignItems="center" 
        paddingHorizontal="$4" 
        paddingVertical="$2"
      >
        <XStack gap="$2">
          {['days', 'weeks', 'months'].map((view) => (
            <TouchableOpacity
              key={view}
              onPress={() => setTimelineView(view as any)}
              style={{
                paddingHorizontal: 12,
                paddingVertical: 6,
                borderRadius: 8,
                backgroundColor: timelineView === view 
                  ? '#3b82f6' 
                  : theme ? '#f3f4f6' : '#374151',
              }}
            >
              <Text style={{
                fontSize: 12,
                fontWeight: '500',
                color: timelineView === view ? '#ffffff' : theme ? '#111827' : '#ffffff',
                textTransform: 'capitalize',
              }}>
                {view}
              </Text>
            </TouchableOpacity>
          ))}
        </XStack>

        <XStack gap="$2" alignItems="center">
          <TouchableOpacity
            onPress={() => setShowCriticalPath(!showCriticalPath)}
            style={{
              paddingHorizontal: 12,
              paddingVertical: 6,
              borderRadius: 8,
              backgroundColor: showCriticalPath 
                ? '#ef4444' 
                : theme ? '#f3f4f6' : '#374151',
            }}
          >
            <Text style={{
              fontSize: 12,
              fontWeight: '500',
              color: showCriticalPath ? '#ffffff' : theme ? '#111827' : '#ffffff',
            }}>
              Critical Path
            </Text>
          </TouchableOpacity>

          <XStack gap="$1">
            <TouchableOpacity
              onPress={() => handleZoom(-0.2)}
              style={{
                padding: 6,
                borderRadius: 6,
                backgroundColor: theme ? '#f3f4f6' : '#374151',
              }}
            >
              <MaterialIcons name="zoom-out" size={16} color={theme ? '#111827' : '#ffffff'} />
            </TouchableOpacity>
            
            <TouchableOpacity
              onPress={() => handleZoom(0.2)}
              style={{
                padding: 6,
                borderRadius: 6,
                backgroundColor: theme ? '#f3f4f6' : '#374151',
              }}
            >
              <MaterialIcons name="zoom-in" size={16} color={theme ? '#111827' : '#ffffff'} />
            </TouchableOpacity>
          </XStack>
        </XStack>
      </XStack>

      {/* Timeline Scale */}
      <ScrollView
        ref={timelineScrollRef}
        horizontal
        showsHorizontalScrollIndicator={false}
        style={{ flex: 1 }}
        onScroll={(event) => {
          const offsetX = event.nativeEvent.contentOffset.x;
          scrollViewRef.current?.scrollTo({ x: offsetX, animated: false });
        }}
        scrollEventThrottle={16}
      >
        <View style={{
          flexDirection: 'row',
          height: 40,
          width: ganttWidth,
          alignItems: 'center',
          backgroundColor: theme ? '#f9fafb' : '#374151',
        }}>
          {timeline.map((date, index) => (
            <View
              key={index}
              style={{
                width: timelineView === 'days' ? layoutConstants.dayWidth : timelineView === 'weeks' ? layoutConstants.weekWidth : layoutConstants.monthWidth,
                height: '100%',
                justifyContent: 'center',
                alignItems: 'center',
                borderRightWidth: 1,
                borderRightColor: theme ? '#d1d5db' : '#4b5563',
              }}
            >
              <Text style={{
                fontSize: 11,
                fontWeight: '500',
                color: theme ? '#6b7280' : '#d1d5db',
              }}>
                {formatTimelineDate(date)}
              </Text>
            </View>
          ))}
        </View>
      </ScrollView>
    </View>
  );

  // Render task row
  const renderTaskRow = (task: Task, index: number) => {
    const position = getTaskPosition(task);
    const isSelected = selectedTask?._id === task._id;
    
    return (
      <View
        key={task._id}
        style={{
          height: layoutConstants.rowHeight,
          flexDirection: 'row',
          borderBottomWidth: 1,
          borderBottomColor: theme ? '#e5e7eb' : '#374151',
          backgroundColor: index % 2 === 0 
            ? (theme ? '#ffffff' : '#1f2937')
            : (theme ? '#f9fafb' : '#111827'),
        }}
      >
        {/* Task Info Column */}
        <View style={{
          width: 250,
          padding: 12,
          justifyContent: 'center',
          borderRightWidth: 1,
          borderRightColor: theme ? '#e5e7eb' : '#374151',
        }}>
          <Text style={{
            fontSize: 14,
            fontWeight: '600',
            color: theme ? '#111827' : '#ffffff',
          }} numberOfLines={1}>
            {task.title}
          </Text>
          
          <XStack alignItems="center" gap="$2" marginTop="$1">
            <View style={{
              width: 6,
              height: 6,
              borderRadius: 3,
              backgroundColor: getPriorityColor(task.priority),
            }} />
            
            <Text style={{
              fontSize: 11,
              color: theme ? '#6b7280' : '#9ca3af',
              textTransform: 'capitalize',
            }}>
              {task.priority}
            </Text>
            
            {task.assignedTo?.length > 0 && (
              <>
                <Text style={{ color: theme ? '#d1d5db' : '#4b5563'}}>•</Text>
                <Text style={{
                  fontSize: 11,
                  color: theme ? '#6b7280' : '#9ca3af',
                }}>
                  {task.assignedTo.length} assigned
                </Text>
              </>
            )}
          </XStack>
        </View>

        {/* Timeline Column */}
        <ScrollView
          ref={index === 0 ? scrollViewRef : undefined}
          horizontal
          showsHorizontalScrollIndicator={false}
          style={{ flex: 1 }}
          onScroll={index === 0 ? (event) => {
            const offsetX = event.nativeEvent.contentOffset.x;
            timelineScrollRef.current?.scrollTo({ x: offsetX, animated: false });
          } : undefined}
          scrollEventThrottle={16}
        >
          <View style={{
            width: ganttWidth,
            height: '100%',
            position: 'relative',
          }}>
            {/* Timeline Grid */}
            <Svg height="100%" width="100%" style={{ position: 'absolute' }}>
              {timeline.map((date, timeIndex) => (
                <Line
                  key={timeIndex}
                  x1={timeIndex * (timelineView === 'days' ? layoutConstants.dayWidth : timelineView === 'weeks' ? layoutConstants.weekWidth : layoutConstants.monthWidth)}
                  y1={0}
                  x2={timeIndex * (timelineView === 'days' ? layoutConstants.dayWidth : timelineView === 'weeks' ? layoutConstants.weekWidth : layoutConstants.monthWidth)}
                  y2={layoutConstants.rowHeight}
                  stroke={theme ? '#e5e7eb' : '#374151'}
                  strokeWidth={0.5}
                />
              ))}
            </Svg>

            {/* Task Bar */}
            <TouchableOpacity
              onPress={() => handleTaskPress(task)}
              style={{
                position: 'absolute',
                left: position.left,
                top: 15,
                width: position.width,
                height: 30,
                backgroundColor: getStatusColor(task.status),
                borderRadius: 15,
                justifyContent: 'center',
                alignItems: 'center',
                borderWidth: isSelected ? 2 : 0,
                borderColor: '#ffffff',
                shadowColor: '#000',
                shadowOffset: { width: 0, height: 2 },
                shadowOpacity: 0.1,
                shadowRadius: 4,
                elevation: 2,
              }}
            >
              <Text style={{
                color: '#ffffff',
                fontSize: 11,
                fontWeight: '600',
              }} numberOfLines={1}>
                {task.title}
              </Text>
              
              {/* Progress Indicator */}
              {task.status === 'in-progress' && (
                <View style={{
                  position: 'absolute',
                  bottom: -2,
                  left: 2,
                  right: 2,
                  height: 2,
                  backgroundColor: 'rgba(255,255,255,0.3)',
                  borderRadius: 1,
                }}>
                  <View style={{
                    height: '100%',
                    width: '60%', // This would come from actual progress data
                    backgroundColor: '#ffffff',
                    borderRadius: 1,
                  }} />
                </View>
              )}
            </TouchableOpacity>

            {/* Dependencies Lines */}
            {task.dependencies?.map((depId) => {
              const depTask = tasks.find(t => t._id === depId);
              if (depTask) {
                const depPosition = getTaskPosition(depTask);
                return (
                  <Svg key={depId} height="100%" width="100%" style={{ position: 'absolute' }}>
                    <Line
                      x1={depPosition.left + depPosition.width}
                      y1={30}
                      x2={position.left}
                      y2={30}
                      stroke="#f59e0b"
                      strokeWidth={2}
                      strokeDasharray="4,4"
                    />
                    <Circle
                      cx={position.left}
                      cy={30}
                      r={3}
                      fill="#f59e0b"
                    />
                  </Svg>
                );
              }
              return null;
            })}
          </View>
        </ScrollView>
      </View>
    );
  };

  // Render work package header
  const renderWorkPackageHeader = (workPackage: WorkPackage) => (
    <View
      key={`wp-${workPackage._id}`}
      style={{
        height: 40,
        backgroundColor: theme ? '#f3f4f6' : '#374151',
        flexDirection: 'row',
        alignItems: 'center',
        borderBottomWidth: 1,
        borderBottomColor: theme ? '#d1d5db' : '#4b5563',
      }}
    >
      <View style={{
        width: 250,
        paddingHorizontal: 12,
        borderRightWidth: 1,
        borderRightColor: theme ? '#d1d5db' : '#4b5563',
      }}>
        <Text style={{
          fontSize: 16,
          fontWeight: '700',
          color: theme ? '#111827' : '#ffffff',
        }}>
          {workPackage.title}
        </Text>
      </View>
      
      <View style={{ flex: 1, paddingHorizontal: 12 }}>
        <Progress 
          value={workPackage.progress} 
          size="$2" 
          backgroundColor={theme ? '#e5e7eb' : '#4b5563'}
        >
          <Progress.Indicator backgroundColor="#3b82f6" />
        </Progress>
      </View>
    </View>
  );

  return (
    <View style={{ 
      flex: 1, 
      backgroundColor: theme ? '#ffffff' : '#111827'
    }}>
      {/* Gantt Header */}
      {renderTimelineHeader()}

      {/* Gantt Content */}
      <ScrollView style={{ flex: 1 }}>
        {workPackages.map((workPackage, wpIndex) => (
          <View key={workPackage._id}>
            {renderWorkPackageHeader(workPackage)}
            {workPackage.tasks?.map((task, taskIndex) => 
              renderTaskRow(task, wpIndex * 100 + taskIndex)
            )}
          </View>
        ))}
        
        {/* Orphaned tasks (not in any work package) */}
        {tasks.filter(task => !workPackages.some(wp => wp.tasks?.some(wpTask => wpTask._id === task._id)))
          .map((task, index) => renderTaskRow(task, 1000 + index))
        }
      </ScrollView>

      {/* Today Indicator */}
      <View style={{
        position: 'absolute',
        top: layoutConstants.headerHeight,
        left: 250 + ((new Date().getTime() - projectDates.startDate.getTime()) / (1000 * 60 * 60 * 24)) * layoutConstants.dayWidth,
        width: 2,
        height: '100%',
        backgroundColor: '#ef4444',
        zIndex: 10,
      }} />
    </View>
  );
};

export default ModernGanttChart;
